import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  ListGroup,
  ListGroupItem,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE = 25 * 1024 * 1024;

const AttachmentModal = ({
  isOpen,
  handleCancel,
  handleSubmit,
  isAllAllowed = false,
  isLoading = false
}) => {
  const [file, setFile] = useState(null);
  const { t } = useTranslation();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    multiple: false,
    accept: isAllAllowed
      ? undefined
      : {
          'image/*': [],
          'application/pdf': []
        },
    maxSize: MAX_FILE_SIZE
  });

  const handleRemoveFile = () => {
    setFile(null);
  };

  const onCancelClick = () => {
    handleCancel();
    setFile(null);
  };

  return (
    <Modal isOpen={isOpen} size="md" centered backdrop>
      <ModalHeader className="d-flex justify-content-center fs-6">{t('attachFile')}</ModalHeader>
      <ModalBody>
        <div
          {...getRootProps()}
          style={{
            border: '2px dashed #ccc',
            borderRadius: '5px',
            padding: '20px',
            textAlign: 'center',
            background: isDragActive ? '#e9f5ff' : '#fafafa',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
          <input {...getInputProps()} />
          <Icon.UploadCloud size={40} />
          <span>
            Drag & drop{isAllAllowed ? ' any file' : ' a PDF or image file'} here, or click to
            select
          </span>
        </div>

        {fileRejections.length > 0 && (
          <div className="text-danger mt-2" style={{ fontSize: '12px' }}>
            {fileRejections[0].errors.map((error) => {
              switch (error.code) {
                case 'file-too-large':
                  return (
                    <div key={error.code}>File is too large. Maximum size allowed is 25MB.</div>
                  );
                case 'file-invalid-type':
                  return (
                    <div key={error.code}>
                      Invalid file type. Only{' '}
                      {isAllAllowed ? 'supported files' : 'PDF or image files'} are allowed.
                    </div>
                  );
                default:
                  return <div key={error.code}>{error.message}</div>;
              }
            })}
          </div>
        )}

        {file && (
          <ListGroup className="mt-3">
            <ListGroupItem className="d-flex justify-content-between align-items-center">
              {file.name}
              <div onClick={handleRemoveFile} className="text-danger cursor-pointer">
                <Icon.Trash2 size={18} />
              </div>
            </ListGroupItem>
          </ListGroup>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-center">
        <Button onClick={onCancelClick} className="px-5 bg-transparent text-primary">
          {t('cancel')}
        </Button>
        <Button
          className="px-5 bg-primary border-primary"
          onClick={() => handleSubmit(file)}
          disabled={!file || isLoading}>
          {t('upload')}
          {isLoading ? <Spinner size="sm" color="light" /> : null}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AttachmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  isAllAllowed: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default AttachmentModal;
